<template>
  <div>
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-qup-blue to-qup-dark text-white">
      <div class="container-max section-padding">
        <div class="text-center">
          <h1 class="text-4xl md:text-5xl font-bold mb-6">
            Contact Us
          </h1>
          <p class="text-xl text-gray-200 max-w-3xl mx-auto">
            Ready to transform your business? Get in touch with our team of experts to discuss your technology needs and discover how we can help you succeed.
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Content -->
    <section class="section-padding">
      <div class="container-max">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Contact Form -->
          <div>
            <h2 class="text-3xl font-bold text-qup-dark mb-6">Get in Touch</h2>
            <form @submit.prevent="submitForm" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    id="firstName"
                    v-model="form.firstName"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-qup-blue focus:border-transparent"
                    placeholder="Your first name"
                  />
                </div>
                <div>
                  <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    id="lastName"
                    v-model="form.lastName"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-qup-blue focus:border-transparent"
                    placeholder="Your last name"
                  />
                </div>
              </div>
              
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-qup-blue focus:border-transparent"
                  placeholder="your.email@company.com"
                />
              </div>
              
              <div>
                <label for="company" class="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <input
                  id="company"
                  v-model="form.company"
                  type="text"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-qup-blue focus:border-transparent"
                  placeholder="Your company name"
                />
              </div>
              
              <div>
                <label for="service" class="block text-sm font-medium text-gray-700 mb-2">
                  Service of Interest *
                </label>
                <select
                  id="service"
                  v-model="form.service"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-qup-blue focus:border-transparent"
                >
                  <option value="">Select a service</option>
                  <option value="security">IT Risk & Security Consultancy</option>
                  <option value="optimization">Process Optimization</option>
                  <option value="development">Software Development & Integration</option>
                  <option value="products">Product Information</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label for="message" class="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  v-model="form.message"
                  rows="5"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-qup-blue focus:border-transparent"
                  placeholder="Tell us about your project or requirements..."
                ></textarea>
              </div>
              
              <button
                type="submit"
                :disabled="isSubmitting"
                class="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ isSubmitting ? 'Sending...' : 'Send Message' }}
              </button>
            </form>
          </div>
          
          <!-- Contact Information -->
          <div>
            <h2 class="text-3xl font-bold text-qup-dark mb-6">Contact Information</h2>
            
            <div class="space-y-8">
              <!-- Office Location -->
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-12 h-12 bg-qup-blue rounded-lg flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-qup-dark mb-2">Office Location</h3>
                  <p class="text-gray-600">
                    Nidakule Ataşehir<br>
                    Barbaros Mah. Begonya Sk. No:1<br>
                    Ataşehir, Istanbul, Turkey<br>
                    <span class="text-sm text-gray-500">Main office and headquarters</span>
                  </p>
                </div>
              </div>
              
              <!-- Email -->
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-12 h-12 bg-qup-blue rounded-lg flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-qup-dark mb-2">Email</h3>
                  <a href="mailto:info@quptechnology.com" class="text-qup-blue hover:text-qup-dark transition-colors">
                    info@quptechnology.com
                  </a>
                  <p class="text-sm text-gray-500 mt-1">We typically respond within 24 hours</p>
                </div>
              </div>
              
              <!-- Phone -->
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-12 h-12 bg-qup-blue rounded-lg flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-qup-dark mb-2">Phone</h3>
                  <a href="tel:+905351078321" class="text-qup-blue hover:text-qup-dark transition-colors">
                    +90 535 107 83 21
                  </a>
                  <p class="text-sm text-gray-500 mt-1">Monday - Friday, 9:00 AM - 6:00 PM</p>
                </div>
              </div>
            </div>
            
            <!-- Business Hours -->
            <div class="mt-12 bg-gray-50 rounded-lg p-6">
              <h3 class="text-xl font-semibold text-qup-dark mb-4">Business Hours</h3>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-gray-600">Monday - Friday</span>
                  <span class="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Saturday - Sunday</span>
                  <span class="font-medium">Closed</span>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'

const isSubmitting = ref(false)

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  service: '',
  message: ''
})

const submitForm = async () => {
  isSubmitting.value = true

  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(form),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit form');
    }

    // Reset form
    Object.assign(form, {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      service: '',
      message: ''
    });

    alert('Thank you for your message! We will get back to you soon.');
  } catch (error) {
    console.error(error);
    alert('Failed to send message. Please try again later.');
  } finally {
    isSubmitting.value = false;
  }
}
</script>
